/**
 * Real-time friends unread counter (private chat unread + incoming friend requests).
 */
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { friendsApi } from './api';
import { playNotificationSound } from './notificationSound';

export default function useFriendsUnread(user) {
  const [data, setData] = useState({ unread_messages: 0, incoming_requests: 0 });
  const socketRef = useRef(null);
  const lastSeenRef = useRef(new Set());

  useEffect(() => {
    if (!user) {
      setData({ unread_messages: 0, incoming_requests: 0 });
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await friendsApi.unreadCount();
        if (!cancelled) setData(res.data || { unread_messages: 0, incoming_requests: 0 });
      } catch { /* silent */ }
    };
    fetchData();

    const interval = setInterval(fetchData, 60000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('register_user', {
        user_id: user.user_id,
        gamertag: user.gamertag,
        name: user.name,
        role: user.role,
      });
    });

    socket.on('private_chat_unread_increment', (payload) => {
      if (cancelled) return;
      if (payload?.sender_id === user.user_id) return;
      const key = `${payload?.friendship_id}:${payload?.created_at}`;
      if (lastSeenRef.current.has(key)) return;
      lastSeenRef.current.add(key);
      if (lastSeenRef.current.size > 200) {
        const first = lastSeenRef.current.values().next().value;
        lastSeenRef.current.delete(first);
      }
      setData(d => ({ ...d, unread_messages: (d.unread_messages || 0) + 1 }));
      playNotificationSound({ pitch: 'low' });
    });

    socket.on('friend_request_received', () => {
      if (cancelled) return;
      setData(d => ({ ...d, incoming_requests: (d.incoming_requests || 0) + 1 }));
      playNotificationSound({ pitch: 'low' });
    });

    socket.on('friend_request_accepted', () => {
      if (cancelled) return;
      // Refresh counts (no obvious counter to update)
      fetchData();
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      try { socket.disconnect(); } catch { /* ignore */ }
    };
  }, [user]);

  const total = (data.unread_messages || 0) + (data.incoming_requests || 0);
  return { ...data, total };
}
